import PropTypes from "prop-types";
import { useEffect, useState, useRef } from "react";
import ArrowEndIcon from "./ArrowEndIcon";
import "../styles.css";
import CopyIcon from "../selection/CopyIcon";
import {
  getCanvasStageHeightPx,
  getMainCanvasWidthPx,
  getOffCanvasStageWidthPx,
  getStageHeightPxFromCanvasHeight,
} from "../../utils/mainCanvasLayout";
import { getOffCanvasMainColumnBounds } from "../../utils/boundaryUtils";
import { clampPolylineGroupToStageBounds } from "../../utils/drawingHtmlUtils";

const TYPING_INPUT_TYPES = new Set([
  "insertText",
  "deleteContentBackward",
  "deleteContentForward",
  "insertCompositionText",
  "insertFromPaste",
  "insertFromDrop",
  "deleteByCut",
]);

const shouldDeferInputUpdate = (inputType) =>
  inputType && TYPING_INPUT_TYPES.has(inputType);

const resolveCanvasHeightPx = (canvasHeight) =>
  Number(canvasHeight) > 100
    ? Number(canvasHeight)
    : getStageHeightPxFromCanvasHeight(canvasHeight);

const clampNumericValue = (value, min, max) => {
  if (value === "" || value === null || value === undefined) {
    return null;
  }
  let num = parseInt(String(value), 10);
  if (Number.isNaN(num)) {
    return null;
  }
  if (typeof min === "number" && num < min) {
    num = min;
  }
  if (typeof max === "number" && num > max) {
    num = max;
  }
  return num;
};

// Helper function to convert negative x/y values to 0 for display only
const getDisplayValue = (value) => {
  if (value === "" || value === null || value === undefined) return value;
  const num = parseFloat(value);
  if (isNaN(num)) return value;
  return num < 0 ? "0" : value;
};

function calculateWaypoints(currentPoints, bendCount) {
  if (!currentPoints || currentPoints.length < 4) {
    return [0, 0, 100, 0];
  }

  const startX = currentPoints[0];
  const startY = currentPoints[1];
  const endX = currentPoints[currentPoints.length - 2];
  const endY = currentPoints[currentPoints.length - 1];

  if (bendCount === 0) {
    return [startX, startY, endX, endY];
  }

  const newPoints = [startX, startY];

  for (let i = 1; i <= bendCount; i++) {
    const t = i / (bendCount + 1);
    const waypointX = startX + (endX - startX) * t;
    const waypointY = startY + (endY - startY) * t;
    newPoints.push(waypointX, waypointY);
  }

  newPoints.push(endX, endY);
  return newPoints;
}

/**
 * Apply preset polyline shape (bends) while keeping the current element's start and end
 * in the same relative positions (group-relative coords). Uses a similarity transform:
 * preset chord → current chord so endpoints match; interior points follow the preset bend pattern.
 */
function mapPresetGeometryToCurrentEndpoints(presetPoints, currentPoints) {
  if (
    !Array.isArray(presetPoints) ||
    presetPoints.length < 4 ||
    !Array.isArray(currentPoints) ||
    currentPoints.length < 4
  ) {
    return presetPoints;
  }

  const psx = presetPoints[0];
  const psy = presetPoints[1];
  const pex = presetPoints[presetPoints.length - 2];
  const pey = presetPoints[presetPoints.length - 1];

  const csx = currentPoints[0];
  const csy = currentPoints[1];
  const cex = currentPoints[currentPoints.length - 2];
  const cey = currentPoints[currentPoints.length - 1];

  const vpx = pex - psx;
  const vpy = pey - psy;
  const vcx = cex - csx;
  const vcy = cey - csy;

  const lenP = Math.hypot(vpx, vpy);
  const lenC = Math.hypot(vcx, vcy);

  if (lenP < 1e-6 || lenC < 1e-6) {
    return [csx, csy, cex, cey];
  }

  const scale = lenC / lenP;
  const angP = Math.atan2(vpy, vpx);
  const angC = Math.atan2(vcy, vcx);
  const delta = angC - angP;
  const cos = Math.cos(delta);
  const sin = Math.sin(delta);

  const out = [];
  for (let i = 0; i < presetPoints.length; i += 2) {
    const x = presetPoints[i];
    const y = presetPoints[i + 1];
    const dx = x - psx;
    const dy = y - psy;
    const rx = scale * (dx * cos - dy * sin);
    const ry = scale * (dx * sin + dy * cos);
    out.push(csx + rx, csy + ry);
  }
  return out;
}

// Compute width/height from points: width = rightmost - leftmost, height = bottommost - topmost
function getBoundingBoxFromPoints(points) {
  if (!points || points.length < 4) return { width: null, height: null };
  let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
  for (let i = 0; i < points.length; i += 2) {
    minX = Math.min(minX, points[i]);
    maxX = Math.max(maxX, points[i]);
    minY = Math.min(minY, points[i + 1]);
    maxY = Math.max(maxY, points[i + 1]);
  }
  return {
    width: maxX - minX,
    height: maxY - minY,
  };
}

// Display value for width/height: use points-based value when available; allow 0 (don't show blank)
function getDisplayWidthHeight(selectedElement) {
  const fromPoints = getBoundingBoxFromPoints(selectedElement?.points);
  const width =
    fromPoints.width !== null
      ? Math.round(fromPoints.width)
      : selectedElement?.width !== undefined && selectedElement?.width !== null
        ? Math.round(selectedElement.width)
        : "";
  const height =
    fromPoints.height !== null
      ? Math.round(fromPoints.height)
      : selectedElement?.height !== undefined && selectedElement?.height !== null
        ? Math.round(selectedElement.height)
        : "";
  return { width, height };
}

const createDefaultUnifiedLineArrowPreset = () => ({
  id: "unified-line-arrow-default-preset",
  name: "Default Line & Arrow",
  type: "arrow",
  stroke: "#FF0000",
  strokeColor: "#FF0000",
  strokeWidth: 2,
  isDashed: false,
  hasStartArrow: false,
  hasEndArrow: true,
  pointerAtBeginning: false,
  pointerAtEnd: true,
  pointerAtEnding: true,
  points: [0, 0, 100, 0],
  width: 100,
  height: 0,
  rotation: 0,
});

// Runtime-only flags should never be persisted in presets or reapplied.
// If restored onto elements, selection visuals can get stuck (e.g. blue control orbs).
const TRANSIENT_UNIFIED_LINE_ARROW_PRESET_KEYS = new Set([
  "_isDragging",
  "_isInOffCanvasDuringDrag",
  "_previewTargetStage",
  "_useNodePositionForGroup",
  "_isCrossCanvasPreview",
  "isPreview",
  "hideBlueCircles",
  "forceShowBlueCircles",
  "forceHideBlueCircles",
  "selected",
  "isSelected",
  "isDraggable",
]);

const sanitizeUnifiedLineArrowPresetVisualProperties = (properties = {}) => {
  if (!properties || typeof properties !== "object") {
    return {};
  }
  return Object.entries(properties).reduce((acc, [key, value]) => {
    if (TRANSIENT_UNIFIED_LINE_ARROW_PRESET_KEYS.has(key)) {
      return acc;
    }
    acc[key] = value;
    return acc;
  }, {});
};

function UnifiedLineArrowEditor({
  selectedElement,
  updateElementProperties,
  onDelete,
  canvasHeight,
  mainCanvasWidthPx,
  onToggleCloneMode = null,
  isCloneMode = false,
}) {
  const DEFAULT_PRESET_STORAGE_KEY = "unifiedLineArrowDefaultStylePreset";
  const DEFAULT_PRESET_ID_STORAGE_KEY = "unifiedLineArrowDefaultStylePresetId";
  const EDITOR_EDGE_BUFFER_PX = 10;
  const MIN_X = 0;
  const MIN_Y = 0;
  const MIN_LENGTH = 1;
  const MAX_LENGTH = 1000;
  const MIN_ANGLE = -360;
  const MAX_ANGLE = 360;
  const MIN_STROKE_WIDTH = 1;
  const MAX_STROKE_WIDTH = 20;
  const MIN_WIDTH = 0;
  const mainStageW = Math.max(
    1,
    Number(mainCanvasWidthPx) || getMainCanvasWidthPx(),
  );
  const MAX_WIDTH = mainStageW;
  const MIN_HEIGHT = 0;
  const MAX_HEIGHT = 600;

  // During drag, keep last known _previewTargetStage so we never fall back to x-based heuristic
  // (which misinterprets coords and shows wrong 0 or 1000+ when _previewTargetStage is briefly undefined).
  const lastPreviewTargetStageRef = useRef(null);
  if (selectedElement?._previewTargetStage === "offCanvas" || selectedElement?._previewTargetStage === "main") {
    lastPreviewTargetStageRef.current = selectedElement._previewTargetStage;
  }
  if (!selectedElement?._isDragging) {
    lastPreviewTargetStageRef.current = null;
  }

  // Determine which stage's coordinates we're showing (for correct x/y display and constraints).
  // During cross-stage drag, Canvas passes _previewTargetStage so x,y are in the PREVIEW stage's space;
  // use it so the editor doesn't mis-infer from raw x (e.g. main line on off-canvas has x~1000 → was showing 1000+; off-canvas line on main has x~0 → was showing 0).
  const isOffCanvas =
    selectedElement?._previewTargetStage === "offCanvas"
      ? true
      : selectedElement?._previewTargetStage === "main"
        ? false
        : selectedElement?.isOffCanvas === true
          ? true
        : selectedElement?._isDragging && lastPreviewTargetStageRef.current != null
          ? lastPreviewTargetStageRef.current === "offCanvas"
          : selectedElement?._isInOffCanvasDuringDrag === true ||
            (selectedElement?._isInOffCanvasDuringDrag === undefined &&
              selectedElement?.x !== undefined &&
              selectedElement?.x >= mainStageW);

  // Calculate proper constraints based on element's bounding box
  const getStageConstraints = () => {
    const edgeInset = EDITOR_EDGE_BUFFER_PX;
    if (!selectedElement?.points || selectedElement.points.length < 4) {
      // Fallback if no points
      if (isOffCanvas) {
        return {
          minX: edgeInset,
          maxX: Math.max(edgeInset, getOffCanvasStageWidthPx() - edgeInset),
          minY: edgeInset,
          maxY: Math.max(edgeInset, getCanvasStageHeightPx() - edgeInset),
        };
      } else {
        const canvasPixelHeight = resolveCanvasHeightPx(canvasHeight);
        return {
          minX: -10000, // Allow negative x values for main canvas stage elements
          maxX: Math.max(edgeInset, mainStageW - edgeInset),
          minY: edgeInset,
          maxY: Math.max(edgeInset, canvasPixelHeight - edgeInset),
        };
      }
    }

    // Calculate bounding box from points
    const points = selectedElement.points;
    let minPointX = Infinity, minPointY = Infinity;
    let maxPointX = -Infinity, maxPointY = -Infinity;

    for (let i = 0; i < points.length; i += 2) {
      minPointX = Math.min(minPointX, points[i]);
      maxPointX = Math.max(maxPointX, points[i]);
      minPointY = Math.min(minPointY, points[i + 1]);
      maxPointY = Math.max(maxPointY, points[i + 1]);
    }

    const strokeWidth = selectedElement.strokeWidth || 2;
    const padding = Math.max(strokeWidth / 2, 5);
    const paddedInset = padding + edgeInset;

    if (isOffCanvas) {
      const stageWidth = getOffCanvasStageWidthPx();
      const stageHeight = getCanvasStageHeightPx();
      // Group position must keep all points within bounds
      // minPointX + groupX >= padding, so groupX >= padding - minPointX
      // maxPointX + groupX <= stageWidth - padding, so groupX <= stageWidth - padding - maxPointX
      return {
        minX: Math.max(0, paddedInset - minPointX),
        maxX: Math.max(0, stageWidth - paddedInset - maxPointX),
        minY: Math.max(0, paddedInset - minPointY),
        maxY: Math.max(0, stageHeight - paddedInset - maxPointY),
      };
    } else {
      const canvasPixelWidth = mainStageW;
      const canvasPixelHeight = resolveCanvasHeightPx(canvasHeight);
      // For main canvas, allow negative x values
      // Use a very negative number as minimum, but still respect the bounding box constraint
      // CRITICAL FIX: Ensure minX allows negative values for main canvas elements
      // The logic should be: groupX >= padding - minPointX OR groupX >= -10000 (effectively unconstrained to left)
      // Since we want to allow negative X, we should use the calculated minX but clamp it to -10000 if needed?
      // Actually, for main canvas, we just want to ensure it doesn't go TOO far off screen?
      // RectangleEditor uses -10000. Let's match that but also consider the visual constraint if desired.
      // But typically we just want to allow infinite negative.

      const calculatedMinX = padding - minPointX;
      return {
        minX: -10000, // Explicitly allow negative values
        maxX: Math.max(edgeInset, canvasPixelWidth - paddedInset - maxPointX),
        minY: Math.max(edgeInset, paddedInset - minPointY),
        maxY: Math.max(edgeInset, canvasPixelHeight - paddedInset - maxPointY),
      };
    }
  };

  const stageConstraints = getStageConstraints();
  const MIN_X_CONSTRAINED = stageConstraints.minX;
  const MAX_X_CONSTRAINED = stageConstraints.maxX;
  const MIN_Y_CONSTRAINED = stageConstraints.minY;
  const MAX_Y_CONSTRAINED = stageConstraints.maxY;
  const activeStageWidth = isOffCanvas ? getOffCanvasStageWidthPx() : mainStageW;
  const activeStageHeight = isOffCanvas
    ? getCanvasStageHeightPx()
    : resolveCanvasHeightPx(canvasHeight);
  const dynamicMaxWidth = Math.max(
    MIN_WIDTH,
    (isOffCanvas
      ? getOffCanvasMainColumnBounds(activeStageWidth).mainZoneStart
      : activeStageWidth) -
      Math.max(0, Number(selectedElement?.x) || 0) -
      EDITOR_EDGE_BUFFER_PX,
  );
  const dynamicMaxHeight = Math.max(
    MIN_HEIGHT,
    activeStageHeight - (Number(selectedElement?.y) || 0),
  );
  const getResizedRelativePoints = (field, sizeValue, currentPoints) => {
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (let i = 0; i < currentPoints.length; i += 2) {
      minX = Math.min(minX, currentPoints[i]);
      maxX = Math.max(maxX, currentPoints[i]);
      minY = Math.min(minY, currentPoints[i + 1]);
      maxY = Math.max(maxY, currentPoints[i + 1]);
    }
    const currentHeight = maxY - minY;
    const currentWidth = maxX - minX;
    const startX = currentPoints.length >= 2 ? currentPoints[0] : minX;
    const startY = currentPoints.length >= 2 ? currentPoints[1] : minY;
    const lastXIndex = currentPoints.length - 2;
    const lastYIndex = currentPoints.length - 1;

    return currentPoints.map((coord, i) => {
      if (i % 2 === 0) {
        if (field === "width") {
          if (sizeValue === 0) return startX;
          if (currentWidth <= 0) {
            return i === lastXIndex ? startX + sizeValue : startX;
          }
          return startX + ((coord - startX) * sizeValue) / currentWidth;
        }
        return coord;
      }
      if (field === "height") {
        if (sizeValue === 0) return startY;
        if (currentHeight <= 0) {
          return i === lastYIndex ? startY + sizeValue : startY;
        }
        return startY + ((coord - startY) * sizeValue) / currentHeight;
      }
      return coord;
    });
  };
  const getBufferedMaxHeight = () => {
    const points = selectedElement?.points;
    if (!points || points.length < 4) {
      return dynamicMaxHeight;
    }
    const elementY = Number(selectedElement?.y) || 0;
    const strokeWidth = Number(selectedElement?.strokeWidth) || 2;
    const paddedInset = Math.max(strokeWidth / 2, 5) + EDITOR_EDGE_BUFFER_PX;
    const stageMinY = paddedInset;
    const stageMaxY = activeStageHeight - paddedInset;
    const heightFitEpsilon = 0.5;

    const fitsHeight = (heightValue) => {
      const resized = getResizedRelativePoints("height", heightValue, points);
      let minAbsY = Infinity;
      let maxAbsY = -Infinity;
      for (let i = 1; i < resized.length; i += 2) {
        const absY = elementY + resized[i];
        minAbsY = Math.min(minAbsY, absY);
        maxAbsY = Math.max(maxAbsY, absY);
      }
      return (
        minAbsY >= stageMinY - heightFitEpsilon &&
        maxAbsY <= stageMaxY + heightFitEpsilon
      );
    };

    if (fitsHeight(dynamicMaxHeight)) {
      // Keep the fast path but normalize to an integer cap so 815.x can become 816
      // when that rounded value still fits within stage bounds.
      const floored = Math.max(MIN_HEIGHT, Math.floor(dynamicMaxHeight));
      const rounded = Math.max(MIN_HEIGHT, Math.round(dynamicMaxHeight));
      return fitsHeight(rounded) ? rounded : floored;
    }

    let low = MIN_HEIGHT;
    let high = Math.max(MIN_HEIGHT, dynamicMaxHeight);
    for (let i = 0; i < 28; i += 1) {
      const mid = (low + high) / 2;
      if (fitsHeight(mid)) {
        low = mid;
      } else {
        high = mid;
      }
    }
    // Avoid off-by-one from floating-point binary search tails.
    const floored = Math.max(MIN_HEIGHT, Math.floor(low));
    const rounded = Math.max(MIN_HEIGHT, Math.round(low));
    return fitsHeight(rounded) ? rounded : floored;
  };
  const getBufferedMaxWidth = () => {
    const points = selectedElement?.points;
    if (!points || points.length < 4) {
      return dynamicMaxWidth;
    }
    const elementX = Number(selectedElement?.x) || 0;
    // Match editor width-input boundary behavior (10px inset), so max width can
    // reach canonical values like 700 instead of getting clipped to 699.
    const paddedInset = EDITOR_EDGE_BUFFER_PX;
    const stageMinX = paddedInset;
    const stageMaxX = isOffCanvas
      ? getOffCanvasMainColumnBounds(activeStageWidth).mainZoneStart - paddedInset
      : activeStageWidth - paddedInset;
    const widthFitEpsilon = 0.5;

    const fitsWidth = (widthValue) => {
      const resized = getResizedRelativePoints("width", widthValue, points);
      let minAbsX = Infinity;
      let maxAbsX = -Infinity;
      for (let i = 0; i < resized.length; i += 2) {
        const absX = elementX + resized[i];
        minAbsX = Math.min(minAbsX, absX);
        maxAbsX = Math.max(maxAbsX, absX);
      }
      return (
        minAbsX >= stageMinX - widthFitEpsilon &&
        maxAbsX <= stageMaxX + widthFitEpsilon
      );
    };


    if (fitsWidth(dynamicMaxWidth)) {
      // Keep the fast path but normalize to an integer cap so 699.x can become 700
      // when that rounded value still fits within stage bounds.
      const floored = Math.max(MIN_WIDTH, Math.floor(dynamicMaxWidth));
      const rounded = Math.max(MIN_WIDTH, Math.round(dynamicMaxWidth));
      return fitsWidth(rounded) ? rounded : floored;
    }

    let low = MIN_WIDTH;
    let high = Math.max(MIN_WIDTH, dynamicMaxWidth);
    for (let i = 0; i < 28; i += 1) {
      const mid = (low + high) / 2;
      if (fitsWidth(mid)) {
        low = mid;
      } else {
        high = mid;
      }
    }
    // Avoid off-by-one from floating-point binary search tails (e.g. 699.999999 -> 699).
    const floored = Math.max(MIN_WIDTH, Math.floor(low));
    const rounded = Math.max(MIN_WIDTH, Math.round(low));
    return fitsWidth(rounded) ? rounded : floored;
  };
  const bufferedMaxWidth = getBufferedMaxWidth();
  const bufferedMaxHeight = getBufferedMaxHeight();

  const canvasPixelHeight = resolveCanvasHeightPx(canvasHeight);
  const MAX_X = getOffCanvasStageWidthPx();
  const MAX_Y = canvasPixelHeight;

  const [elementPresets, setElementPresets] = useState(() => {
    const saved = localStorage.getItem("sharedPresets");
    if (saved) {
      const parsed = JSON.parse(saved);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }
    return [createDefaultUnifiedLineArrowPreset()];
  });
  const [defaultPresetId, setDefaultPresetId] = useState(() =>
    localStorage.getItem(DEFAULT_PRESET_ID_STORAGE_KEY),
  );
  const [editingPreset, setEditingPreset] = useState(null);
  const [pendingDefaultPresetId, setPendingDefaultPresetId] = useState(null);
  const [defaultPresetIdBeforeEdit, setDefaultPresetIdBeforeEdit] =
    useState(null);
  const [pendingNewPresetId, setPendingNewPresetId] = useState(null);
  const [isElementPresetsExpanded, setIsElementPresetsExpanded] =
    useState(true);
  const [isPropertiesExpanded, setIsPropertiesExpanded] = useState(true);

  // Determine element type
  const hasStartArrow = selectedElement?.hasStartArrow || false;
  const hasEndArrow = selectedElement?.hasEndArrow || false;
  const isDoubleSidedArrow = hasStartArrow && hasEndArrow;
  const isSingleSidedArrow = hasStartArrow && !hasEndArrow;
  const isLine = !hasStartArrow && !hasEndArrow;

  const { width: initialWidth, height: initialHeight } = getDisplayWidthHeight(selectedElement);
  const stageSpanMaxWidth = Math.max(
    MIN_WIDTH,
    (isOffCanvas
      ? getOffCanvasMainColumnBounds(activeStageWidth).mainZoneStart
      : activeStageWidth) -
      2 * EDITOR_EDGE_BUFFER_PX,
  );
  const safeBufferedMaxWidth = Math.max(
    MIN_WIDTH,
    bufferedMaxWidth,
    dynamicMaxWidth,
    stageSpanMaxWidth,
  );
  const safeBufferedMaxHeight = Math.max(
    MIN_HEIGHT,
    bufferedMaxHeight,
    Number(initialHeight) || MIN_HEIGHT,
  );
  const [localInputValues, setLocalInputValues] = useState({
    x: selectedElement?.x !== undefined && selectedElement?.x !== null
      ? (isOffCanvas ? Math.max(stageConstraints.minX, Math.round(selectedElement.x)) : Math.round(selectedElement.x)) : "",
    y: selectedElement?.y !== undefined && selectedElement?.y !== null
      ? Math.max(0, Math.round(selectedElement.y)) : "",
    width: initialWidth,
    height: initialHeight,
    strokeWidth: selectedElement?.strokeWidth
      ? Math.round(selectedElement.strokeWidth)
      : "",
    bends: selectedElement?.points
      ? Math.max(0, (selectedElement.points.length - 4) / 2)
      : 0,
    colorHex:
      selectedElement?.stroke || selectedElement?.strokeColor
        ? (selectedElement.stroke || selectedElement.strokeColor)
          .toUpperCase()
          .replace("#", "")
        : "000000",
    hasStartArrow: selectedElement?.hasStartArrow || false,
    hasEndArrow: selectedElement?.hasEndArrow || false,
  });

  useEffect(() => {
    if (selectedElement) {
      // Recalculate constraints; use _previewTargetStage during cross-stage drag so constraints match the coordinate system of x,y.
      // When _isDragging and _previewTargetStage is undefined, use last known stage from ref to avoid wrong x display.
      const currentIsOffCanvas =
        selectedElement?._previewTargetStage === "offCanvas"
          ? true
          : selectedElement?._previewTargetStage === "main"
            ? false
            : selectedElement?.isOffCanvas === true
              ? true
            : selectedElement?._isDragging && lastPreviewTargetStageRef.current != null
              ? lastPreviewTargetStageRef.current === "offCanvas"
              : selectedElement?._isInOffCanvasDuringDrag === true ||
                (selectedElement?._isInOffCanvasDuringDrag === undefined &&
                  selectedElement?.x !== undefined &&
                  selectedElement?.x >= mainStageW);

      const currentConstraints = (() => {
        if (!selectedElement?.points || selectedElement.points.length < 4) {
          if (currentIsOffCanvas) {
            return {
              minX: 0,
              maxX: getOffCanvasStageWidthPx(),
              minY: 0,
              maxY: getCanvasStageHeightPx(),
            };
          } else {
            const canvasPixelHeight = resolveCanvasHeightPx(canvasHeight);
            return { minX: -10000, maxX: mainStageW, minY: 0, maxY: canvasPixelHeight };
          }
        }

        const points = selectedElement.points;
        let minPointX = Infinity, minPointY = Infinity;
        let maxPointX = -Infinity, maxPointY = -Infinity;

        for (let i = 0; i < points.length; i += 2) {
          minPointX = Math.min(minPointX, points[i]);
          maxPointX = Math.max(maxPointX, points[i]);
          minPointY = Math.min(minPointY, points[i + 1]);
          maxPointY = Math.max(maxPointY, points[i + 1]);
        }

        const strokeWidth = selectedElement.strokeWidth || 2;
        const padding = Math.max(strokeWidth / 2, 5);

        if (currentIsOffCanvas) {
          const stageWidth = getOffCanvasStageWidthPx();
          const stageHeight = getCanvasStageHeightPx();
          return {
            minX: Math.max(0, padding - minPointX),
            maxX: Math.max(0, stageWidth - padding - maxPointX),
            minY: Math.max(0, padding - minPointY),
            maxY: Math.max(0, stageHeight - padding - maxPointY),
          };
        } else {
          const canvasPixelWidth = mainStageW;
          const canvasPixelHeight = resolveCanvasHeightPx(canvasHeight);
          // For main canvas, allow negative x values
          const calculatedMinX = padding - minPointX;
          return {
            minX: Math.min(-10000, calculatedMinX), // Allow negative x values for main canvas stage elements
            maxX: Math.max(0, canvasPixelWidth - padding - maxPointX),
            minY: Math.max(0, padding - minPointY),
            maxY: Math.max(0, canvasPixelHeight - padding - maxPointY),
          };
        }
      })();

      // Constrain x and y to be within bounds (x can be negative for main canvas)
      const constrainedX = selectedElement.x !== undefined && selectedElement.x !== null
        ? Math.max(currentConstraints.minX, Math.min(currentConstraints.maxX, Math.round(selectedElement.x)))
        : "";
      const constrainedY = selectedElement.y !== undefined && selectedElement.y !== null
        ? Math.max(currentConstraints.minY, Math.min(currentConstraints.maxY, Math.round(selectedElement.y)))
        : "";

      const { width: displayWidth, height: displayHeight } = getDisplayWidthHeight(selectedElement);

      setLocalInputValues({
        x: constrainedX,
        y: constrainedY,
        width: displayWidth,
        height: displayHeight,
        strokeWidth: selectedElement.strokeWidth
          ? Math.round(selectedElement.strokeWidth)
          : "",
        bends: selectedElement.points
          ? Math.max(0, (selectedElement.points.length - 4) / 2)
          : 0,
        colorHex:
          selectedElement.stroke || selectedElement.strokeColor
            ? (selectedElement.stroke || selectedElement.strokeColor)
              .toUpperCase()
              .replace("#", "")
            : "000000",
        hasStartArrow: selectedElement.hasStartArrow || false,
        hasEndArrow: selectedElement.hasEndArrow || false,
      });
    }
  }, [selectedElement, selectedElement?._previewTargetStage, selectedElement?._isDragging, selectedElement?._isInOffCanvasDuringDrag, selectedElement?.x, selectedElement?.y]);

  const validateAndParseHexColor = (hexValue) => {
    let hex = hexValue.trim().replace(/^#/, "").toUpperCase();

    if (!/^[0-9A-F]{3}$|^[0-9A-F]{6}$/.test(hex)) {
      return null;
    }

    if (hex.length === 3) {
      hex = hex
        .split("")
        .map((char) => char + char)
        .join("");
    }

    return `#${hex}`;
  };

  const handleNumberInputWithLimits = (field, value, min, max) => {
    let num = parseInt(value, 10);
    if (value === "" || isNaN(num)) {
      updateElementProperties(selectedElement.id, { [field]: min });
      return;
    }
    if (typeof min === "number" && num < min) num = min;
    if (typeof max === "number" && num > max) num = max;
    if (field === "y") {
    }

    if (field === "bends") {

      const bendCount = Math.max(0, Math.floor(num));
      const currentPoints = selectedElement.points || [0, 0, 0, 0];
      const elementX = selectedElement.x || 0;
      const elementY = selectedElement.y || 0;


      // Convert relative points to absolute coordinates
      // This ensures we work with actual positions regardless of how points are stored
      const absolutePoints = [];
      for (let i = 0; i < currentPoints.length; i += 2) {
        absolutePoints.push(
          currentPoints[i] + elementX,
          currentPoints[i + 1] + elementY
        );
      }

      // Calculate current absolute bounding box to preserve the CENTER
      let currentAbsMinX = Infinity,
        currentAbsMinY = Infinity,
        currentAbsMaxX = -Infinity,
        currentAbsMaxY = -Infinity;
      for (let i = 0; i < absolutePoints.length; i += 2) {
        currentAbsMinX = Math.min(currentAbsMinX, absolutePoints[i]);
        currentAbsMaxX = Math.max(currentAbsMaxX, absolutePoints[i]);
        currentAbsMinY = Math.min(currentAbsMinY, absolutePoints[i + 1]);
        currentAbsMaxY = Math.max(currentAbsMaxY, absolutePoints[i + 1]);
      }

      // Calculate current bounding box center (absolute coordinates)
      const currentCenterAbsX = (currentAbsMinX + currentAbsMaxX) / 2;
      const currentCenterAbsY = (currentAbsMinY + currentAbsMaxY) / 2;


      // Get absolute start and end points
      const absStartX = absolutePoints[0];
      const absStartY = absolutePoints[1];
      const absEndX = absolutePoints[absolutePoints.length - 2];
      const absEndY = absolutePoints[absolutePoints.length - 1];

      // Calculate new waypoints in absolute coordinates
      const newAbsolutePoints = [];
      newAbsolutePoints.push(absStartX, absStartY);

      if (bendCount > 0) {
        for (let i = 1; i <= bendCount; i++) {
          const t = i / (bendCount + 1);
          const waypointX = absStartX + (absEndX - absStartX) * t;
          const waypointY = absStartY + (absEndY - absStartY) * t;
          newAbsolutePoints.push(waypointX, waypointY);
        }
      }

      newAbsolutePoints.push(absEndX, absEndY);


      // Calculate new absolute bounding box
      let newAbsMinX = Infinity,
        newAbsMinY = Infinity,
        newAbsMaxX = -Infinity,
        newAbsMaxY = -Infinity;
      for (let i = 0; i < newAbsolutePoints.length; i += 2) {
        newAbsMinX = Math.min(newAbsMinX, newAbsolutePoints[i]);
        newAbsMaxX = Math.max(newAbsMaxX, newAbsolutePoints[i]);
        newAbsMinY = Math.min(newAbsMinY, newAbsolutePoints[i + 1]);
        newAbsMaxY = Math.max(newAbsMaxY, newAbsolutePoints[i + 1]);
      }

      // Calculate new absolute bounding box center
      const newCenterAbsX = (newAbsMinX + newAbsMaxX) / 2;
      const newCenterAbsY = (newAbsMinY + newAbsMaxY) / 2;


      // Calculate offset needed to preserve absolute center position
      const offsetX = currentCenterAbsX - newCenterAbsX;
      const offsetY = currentCenterAbsY - newCenterAbsY;


      // Adjust element position to preserve absolute center
      const newElementX = elementX + offsetX;
      const newElementY = elementY + offsetY;

      // Convert absolute points back to relative coordinates relative to newElementX/Y
      const offsetPoints = [];
      for (let i = 0; i < newAbsolutePoints.length; i += 2) {
        offsetPoints.push(
          newAbsolutePoints[i] - newElementX,
          newAbsolutePoints[i + 1] - newElementY
        );
      }


      // Calculate bounding box from relative points
      let relMinX = Infinity,
        relMinY = Infinity,
        relMaxX = -Infinity,
        relMaxY = -Infinity;
      for (let i = 0; i < offsetPoints.length; i += 2) {
        relMinX = Math.min(relMinX, offsetPoints[i]);
        relMaxX = Math.max(relMaxX, offsetPoints[i]);
        relMinY = Math.min(relMinY, offsetPoints[i + 1]);
        relMaxY = Math.max(relMaxY, offsetPoints[i + 1]);
      }

      // Verify absolute center position is preserved
      const relCenterX = (relMinX + relMaxX) / 2;
      const relCenterY = (relMinY + relMaxY) / 2;
      const finalCenterAbsX = relCenterX + newElementX;
      const finalCenterAbsY = relCenterY + newElementY;
      const positionPreserved =
        Math.abs(finalCenterAbsX - currentCenterAbsX) < 0.01 &&
        Math.abs(finalCenterAbsY - currentCenterAbsY) < 0.01;

      const newWidth = relMaxX - relMinX;
      const newHeight = relMaxY - relMinY;



      updateElementProperties(selectedElement.id, {
        points: offsetPoints,
        width: newWidth,
        height: newHeight,
        x: newElementX,
        y: newElementY,
        skipOffset: true, // Prevent further position recalculation
      });

      return;
    }

    if ((field === "width" || field === "height") && selectedElement.points) {
      const currentPoints = selectedElement.points;
      const currentX = selectedElement.x || 0;
      const currentY = selectedElement.y || 0;

      // Compute current bounding box from points (works regardless of which point is top/bottom or left/right)
      let minX = Infinity, minY = Infinity, maxX = -Infinity, maxY = -Infinity;
      for (let i = 0; i < currentPoints.length; i += 2) {
        minX = Math.min(minX, currentPoints[i]);
        maxX = Math.max(maxX, currentPoints[i]);
        minY = Math.min(minY, currentPoints[i + 1]);
        maxY = Math.max(maxY, currentPoints[i + 1]);
      }
      const currentHeight = maxY - minY;
      const currentWidth = maxX - minX;

      // Always keep start point fixed; only the end (and bends) shift.
      // When width/height is 0: bring end point (and bends) to start point.
      // When increasing from 0: move only the end point away from start by num.
      // When scaling: anchor at start point so end arrow shifts, not start.
      const startX = currentPoints.length >= 2 ? currentPoints[0] : minX;
      const startY = currentPoints.length >= 2 ? currentPoints[1] : minY;
      const lastXIndex = currentPoints.length - 2;
      const lastYIndex = currentPoints.length - 1;
      if (field === "width") {
        num = Math.max(MIN_WIDTH, Math.min(safeBufferedMaxWidth, num));
      }
      if (field === "height") {
        num = Math.max(MIN_HEIGHT, Math.min(safeBufferedMaxHeight, num));
      }
      const newRelativePoints = getResizedRelativePoints(field, num, currentPoints);

      // If size increase would overflow stage by 1px due current anchor position,
      // shift element position within editor inset bounds instead of shrinking size.
      let relMinX = Infinity, relMinY = Infinity, relMaxX = -Infinity, relMaxY = -Infinity;
      for (let i = 0; i < newRelativePoints.length; i += 2) {
        relMinX = Math.min(relMinX, newRelativePoints[i]);
        relMaxX = Math.max(relMaxX, newRelativePoints[i]);
        relMinY = Math.min(relMinY, newRelativePoints[i + 1]);
        relMaxY = Math.max(relMaxY, newRelativePoints[i + 1]);
      }

      const stageMinX = EDITOR_EDGE_BUFFER_PX;
      const stageMaxX = isOffCanvas
        ? getOffCanvasMainColumnBounds(activeStageWidth).mainZoneStart - EDITOR_EDGE_BUFFER_PX
        : activeStageWidth - EDITOR_EDGE_BUFFER_PX;
      const stageMinY = EDITOR_EDGE_BUFFER_PX;
      const stageMaxY = activeStageHeight - EDITOR_EDGE_BUFFER_PX;

      const allowedMinX = stageMinX - relMinX;
      const allowedMaxX = stageMaxX - relMaxX;
      const allowedMinY = stageMinY - relMinY;
      const allowedMaxY = stageMaxY - relMaxY;

      const clampedX = Math.min(Math.max(currentX, allowedMinX), allowedMaxX);
      const clampedY = Math.min(Math.max(currentY, allowedMinY), allowedMaxY);

      const newWidth = field === "width" ? num : currentWidth;
      const newHeight = field === "height" ? num : currentHeight;

      updateElementProperties(selectedElement.id, {
        [field]: num,
        points: newRelativePoints,
        x: clampedX,
        y: clampedY,
        width: newWidth,
        height: newHeight,
        skipOffset: true,
      });
    } else {
      updateElementProperties(selectedElement.id, { [field]: num });
    }
  };

  const handleEnterKey = (event) => {
    if (event.key === "Enter") {
      event.preventDefault();
      event.target.blur();
    }
  };

  const handleArrowTypeChange = (checkboxName, checked, event) => {
    // Prevent double-firing by stopping event propagation if it came from a click
    if (event) {
      event.stopPropagation();
    }

    // Use selectedElement directly instead of localInputValues to avoid stale state
    const currentHasStartArrow = selectedElement?.hasStartArrow === true;
    const currentHasEndArrow = selectedElement?.hasEndArrow === true;

    let newHasStartArrow = currentHasStartArrow;
    let newHasEndArrow = currentHasEndArrow;

    // Independent checkboxes - each controls its own arrow
    if (checkboxName === "hasStartArrow") {
      newHasStartArrow = checked === true;
    } else if (checkboxName === "hasEndArrow") {
      newHasEndArrow = checked === true;
    }

    // Ensure both values are explicitly boolean
    newHasStartArrow = Boolean(newHasStartArrow);
    newHasEndArrow = Boolean(newHasEndArrow);

    // Only update if there's an actual change
    if (
      newHasStartArrow === currentHasStartArrow &&
      newHasEndArrow === currentHasEndArrow
    ) {
      return; // No change needed
    }

    // Determine the new type based on arrow flags
    let newType;
    if (newHasStartArrow && newHasEndArrow) {
      newType = "DoubleSidedArrow";
    } else if (newHasStartArrow || newHasEndArrow) {
      newType = "Arrow";
    } else {
      newType = "Line";
    }

    // Update local state immediately for UI responsiveness
    setLocalInputValues((prev) => ({
      ...prev,
      hasStartArrow: newHasStartArrow,
      hasEndArrow: newHasEndArrow,
    }));

    // Update element properties - preserve position and geometry
    // CRITICAL: Only update arrow flags and type, don't pass points/x/y/width/height
    // This preserves the existing relative points and position from the lines array
    // Passing points from selectedElement (which are absolute) causes position jumps
    // CRITICAL: Explicitly set both flags to boolean values to ensure false values are preserved
    updateElementProperties(selectedElement.id, {
      hasStartArrow: Boolean(newHasStartArrow),
      hasEndArrow: Boolean(newHasEndArrow),
      type: newType,
      // CRITICAL: Keep legacy arrow flags in sync.
      // Many parts of the canvas still treat pointerAtEnd/pointerAtEnding/pointerAtBeginning
      // as the source of truth. If we only flip hasEndArrow=false but leave pointerAtEnd=true,
      // clones and renderers will still behave like an arrow.
      pointerAtBeginning: Boolean(newHasStartArrow),
      pointerAtEnd: Boolean(newHasEndArrow),
      pointerAtEnding: Boolean(newHasEndArrow),
      // Don't pass points, x, y, width, height - let the existing values in the lines array be preserved
      // skipOffset is not needed since we're not passing points
    });
  };

  const saveElementPresetsToStorage = (presets) => {
    try {
      localStorage.setItem("sharedPresets", JSON.stringify(presets));
    } catch (error) {
      alert("Failed to save preset. Please check the console for details.");
    }
  };

  const saveDefaultPresetToStorage = (preset) => {
    localStorage.setItem(DEFAULT_PRESET_STORAGE_KEY, JSON.stringify(preset));
    localStorage.setItem(DEFAULT_PRESET_ID_STORAGE_KEY, String(preset.id));
    setDefaultPresetId(String(preset.id));
    // `storage` events don't fire in the same tab, so emit a local event
    // to update consumers like Toolbar immediately.
    if (typeof window !== "undefined") {
      window.dispatchEvent(
        new CustomEvent("unifiedLineArrowDefaultPresetChanged", {
          detail: {
            hasStartArrow: Boolean(
              preset?.hasStartArrow || preset?.pointerAtBeginning,
            ),
            hasEndArrow: Boolean(
              preset?.hasEndArrow ||
                preset?.pointerAtEnd ||
                preset?.pointerAtEnding,
            ),
          },
        }),
      );
    }
  };

  useEffect(() => {
    if (!Array.isArray(elementPresets) || elementPresets.length === 0) {
      const fallback = createDefaultUnifiedLineArrowPreset();
      const next = [fallback];
      setElementPresets(next);
      saveElementPresetsToStorage(next);
      saveDefaultPresetToStorage(fallback);
      return;
    }
    const storedId = localStorage.getItem(DEFAULT_PRESET_ID_STORAGE_KEY);
    const hasStoredDefault = elementPresets.some(
      (preset) => String(preset.id) === String(storedId),
    );
    if (!storedId || !hasStoredDefault) {
      saveDefaultPresetToStorage(elementPresets[0]);
    }
  }, [elementPresets]);

  const saveCurrentElementStyleAsPreset = () => {
    if (editingPreset) return;
    const usedNames = new Set(elementPresets.map((p) => p.name));

    let index = 1;
    let name;
    do {
      name = `${isLine ? "Line" : isDoubleSidedArrow ? "Double-Sided Arrow" : "Arrow"
        } Preset ${index}`;
      index++;
    } while (usedNames.has(name));

    const {
      x,
      y,
      id,
      name: originalName,
      ...styleProperties
    } = selectedElement;

    const preset = {
      type: isLine
        ? "line"
        : isDoubleSidedArrow
          ? "double_sided_arrow"
          : "arrow",
      ...sanitizeUnifiedLineArrowPresetVisualProperties(styleProperties),
      id: Date.now() + Math.random(),
      name,
    };

    const updatedPresets = [...elementPresets, preset];
    setElementPresets(updatedPresets);
    setEditingPreset(preset);
    setPendingDefaultPresetId(defaultPresetId);
    setDefaultPresetIdBeforeEdit(defaultPresetId);
    setPendingNewPresetId(preset.id);
  };

  const applyElementPreset = (preset) => {
    const {
      points,
      width,
      height,
      x,
      y,
      rotation,
      id,
      name,
      type,
      ...visualProperties
    } = preset;

    // Apply only visual style fields from the preset.
    // Apply visual fields and preset geometry (points/bends), while keeping current element position.
    const nextProps = {
      ...sanitizeUnifiedLineArrowPresetVisualProperties(visualProperties),
      id: selectedElement.id,
      name: selectedElement.name,
    };

    if (Array.isArray(points) && points.length >= 4) {
      const currentPts = selectedElement.points;
      const mergedPoints =
        Array.isArray(currentPts) && currentPts.length >= 4
          ? mapPresetGeometryToCurrentEndpoints(points, currentPts)
          : points;
      const strokeWidth = Number(selectedElement?.strokeWidth ?? nextProps?.strokeWidth ?? 2) || 2;
      const edgeInset = EDITOR_EDGE_BUFFER_PX;
      const rawCandidate = {
        ...selectedElement,
        ...nextProps,
        points: mergedPoints,
        x: Number(selectedElement?.x) || 0,
        y: Number(selectedElement?.y) || 0,
      };
      const stageClamped = clampPolylineGroupToStageBounds(
        rawCandidate,
        activeStageWidth,
        activeStageHeight,
        strokeWidth,
        edgeInset,
      );
      let finalCandidate = stageClamped;
      if (isOffCanvas && Array.isArray(stageClamped?.points) && stageClamped.points.length >= 4) {
        const { mainZoneStart } = getOffCanvasMainColumnBounds(activeStageWidth);
        const edgePad = Math.max(0, strokeWidth / 2) + edgeInset;
        let minAbsX = Infinity;
        let maxAbsX = -Infinity;
        const baseX = Number(stageClamped?.x) || 0;
        for (let i = 0; i < stageClamped.points.length; i += 2) {
          const absX = baseX + (Number(stageClamped.points[i]) || 0);
          minAbsX = Math.min(minAbsX, absX);
          maxAbsX = Math.max(maxAbsX, absX);
        }
        const leftMinAbs = edgePad;
        const leftMaxAbs = mainZoneStart - edgePad;
        let shiftX = 0;
        if (minAbsX < leftMinAbs) shiftX = leftMinAbs - minAbsX;
        else if (maxAbsX > leftMaxAbs) shiftX = leftMaxAbs - maxAbsX;
        if (shiftX !== 0) {
          finalCandidate = {
            ...stageClamped,
            x: (Number(stageClamped?.x) || 0) + shiftX,
          };
        }
      }
      nextProps.points = finalCandidate.points;
      nextProps.x = Number(finalCandidate.x) || 0;
      nextProps.y = Number(finalCandidate.y) || 0;
      const bb = getBoundingBoxFromPoints(finalCandidate.points);
      if (bb.width !== null && Number.isFinite(bb.width)) {
        nextProps.width = bb.width;
      }
      if (bb.height !== null && Number.isFinite(bb.height)) {
        nextProps.height = bb.height;
      }
      nextProps.skipOffset = true;
    }

    updateElementProperties(selectedElement.id, nextProps);
  };

  const x = Number.isFinite(selectedElement.x || selectedElement.newX)
    ? Math.round(selectedElement.x || selectedElement.newX)
    : 0;
  const y = Number.isFinite(selectedElement.y || selectedElement.newY)
    ? Math.round(selectedElement.y || selectedElement.newY)
    : 0;
  const length = Number.isFinite(selectedElement.length)
    ? Math.round(selectedElement.length)
    : 100;
  const angle = Number.isFinite(selectedElement.angle)
    ? Math.round(selectedElement.angle)
    : 0;
  const strokeColor =
    selectedElement.stroke || selectedElement.strokeColor || "#000000";
  const strokeWidth = selectedElement.strokeWidth || 2;
  const isDashed = selectedElement.isDashed || false;

  return (
    <div>
      {/* Position and Dimensions */}
      <div
        style={{
          background: "#f5f5f5",
          borderRadius: "8px",
          padding: "10px 12px",
          marginBottom: "12px",
        }}
      >
        {/* Row 1: Position */}
        <div
          style={{
            display: "flex",
            gap: "16px",
            alignItems: "center",
            marginBottom: "8px",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <label style={{ whiteSpace: "nowrap" }}>XPos</label>
            <input
              type="number"
              value={getDisplayValue(localInputValues.x)}
              min={MIN_X_CONSTRAINED}
              max={MAX_X_CONSTRAINED}
              onKeyDown={handleEnterKey}
              onChange={(e) => {
                const { value } = e.target;
                const inputType = e.nativeEvent?.inputType;
                setLocalInputValues((prev) => ({
                  ...prev,
                  x: value,
                }));
                if (value === "" || shouldDeferInputUpdate(inputType)) {
                  return;
                }
                const clamped = clampNumericValue(value, MIN_X_CONSTRAINED, MAX_X_CONSTRAINED);
                if (clamped === null) {
                  return;
                }
                // CRITICAL FIX: Ensure clamped value respects constraints
                const finalValue = Math.max(MIN_X_CONSTRAINED, Math.min(MAX_X_CONSTRAINED, clamped));
                handleNumberInputWithLimits("x", String(finalValue), MIN_X_CONSTRAINED, MAX_X_CONSTRAINED);
                setLocalInputValues((prev) => ({
                  ...prev,
                  x: finalValue,
                }));
              }}
              onBlur={(e) => {
                const val = e.target.value;
                if (val === "") {
                  handleNumberInputWithLimits("x", String(MIN_X_CONSTRAINED), MIN_X_CONSTRAINED, MAX_X_CONSTRAINED);
                } else {
                  const clamped = clampNumericValue(val, MIN_X_CONSTRAINED, MAX_X_CONSTRAINED);
                  if (clamped !== null) {
                    const finalValue = Math.max(MIN_X_CONSTRAINED, Math.min(MAX_X_CONSTRAINED, clamped));
                    handleNumberInputWithLimits("x", String(finalValue), MIN_X_CONSTRAINED, MAX_X_CONSTRAINED);
                  }
                }
              }}
              style={{
                width: "60px",
                padding: "6px 8px",
                marginLeft: "7px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <label style={{ whiteSpace: "nowrap" }}>YPos</label>
            <input
              type="number"
              value={getDisplayValue(localInputValues.y)}
              min={MIN_Y_CONSTRAINED}
              max={MAX_Y_CONSTRAINED}
              onKeyDown={handleEnterKey}
              onChange={(e) => {
                const { value } = e.target;
                const inputType = e.nativeEvent?.inputType;
                setLocalInputValues((prev) => ({
                  ...prev,
                  y: value,
                }));
                if (value === "" || shouldDeferInputUpdate(inputType)) {
                  return;
                }
                const clamped = clampNumericValue(value, MIN_Y_CONSTRAINED, MAX_Y_CONSTRAINED);
                if (clamped === null) {
                  return;
                }
                // CRITICAL FIX: Ensure clamped value respects constraints
                const finalValue = Math.max(MIN_Y_CONSTRAINED, Math.min(MAX_Y_CONSTRAINED, clamped));
                handleNumberInputWithLimits("y", String(finalValue), MIN_Y_CONSTRAINED, MAX_Y_CONSTRAINED);
                setLocalInputValues((prev) => ({
                  ...prev,
                  y: finalValue,
                }));
              }}
              onBlur={(e) => {
                const val = e.target.value;
                if (val === "") {
                  handleNumberInputWithLimits("y", String(MIN_Y_CONSTRAINED), MIN_Y_CONSTRAINED, MAX_Y_CONSTRAINED);
                } else {
                  const clamped = clampNumericValue(val, MIN_Y_CONSTRAINED, MAX_Y_CONSTRAINED);
                  if (clamped !== null) {
                    const finalValue = Math.max(MIN_Y_CONSTRAINED, Math.min(MAX_Y_CONSTRAINED, clamped));
                    handleNumberInputWithLimits("y", String(finalValue), MIN_Y_CONSTRAINED, MAX_Y_CONSTRAINED);
                  }
                }
              }}
              style={{
                width: "60px",
                padding: "6px 8px",
                marginLeft: "10px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            />
          </div>
        </div>

        {/* Row 2: Dimensions */}
        <div
          style={{
            display: "flex",
            gap: "16px",
            alignItems: "center",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <label style={{ whiteSpace: "nowrap" }}>Width</label>
            <input
              type="number"
              value={localInputValues.width}
              min={MIN_WIDTH}
              max={safeBufferedMaxWidth}
              onKeyDown={handleEnterKey}
              onChange={(e) => {
                const { value } = e.target;
                const inputType = e.nativeEvent?.inputType;
                setLocalInputValues((prev) => ({
                  ...prev,
                  width: value,
                }));
                if (value === "" || shouldDeferInputUpdate(inputType)) {
                  return;
                }
                const bounded = clampNumericValue(
                  value,
                  MIN_WIDTH,
                  safeBufferedMaxWidth,
                );
                if (bounded === null) {
                  return;
                }
                handleNumberInputWithLimits(
                  "width",
                  String(bounded),
                  MIN_WIDTH,
                  safeBufferedMaxWidth
                );
                setLocalInputValues((prev) => ({
                  ...prev,
                  width: bounded,
                }));
              }}
              onBlur={(e) => {
                const val = e.target.value;
                if (val === "") {
                  handleNumberInputWithLimits(
                    "width",
                    String(MIN_WIDTH),
                    MIN_WIDTH,
                    safeBufferedMaxWidth
                  );
                } else {
                  const clamped = clampNumericValue(
                    val,
                    MIN_WIDTH,
                    safeBufferedMaxWidth,
                  );
                  if (clamped === null) {
                    return;
                  }
                  handleNumberInputWithLimits(
                    "width",
                    String(clamped),
                    MIN_WIDTH,
                    safeBufferedMaxWidth
                  );
                }
              }}
              style={{
                width: "60px",
                padding: "6px 8px",
                marginLeft: "3px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: "4px" }}>
            <label style={{ whiteSpace: "nowrap" }}>Height</label>
            <input
              type="number"
              value={localInputValues.height}
              min={MIN_HEIGHT}
              max={safeBufferedMaxHeight}
              onKeyDown={handleEnterKey}
              onChange={(e) => {
                const { value } = e.target;
                const inputType = e.nativeEvent?.inputType;
                setLocalInputValues((prev) => ({
                  ...prev,
                  height: value,
                }));
                if (value === "" || shouldDeferInputUpdate(inputType)) {
                  return;
                }
                const clamped = clampNumericValue(
                  value,
                  MIN_HEIGHT,
                  safeBufferedMaxHeight
                );
                if (clamped === null) {
                  return;
                }
                handleNumberInputWithLimits(
                  "height",
                  String(clamped),
                  MIN_HEIGHT,
                  safeBufferedMaxHeight
                );
                setLocalInputValues((prev) => ({
                  ...prev,
                  height: clamped,
                }));
              }}
              onBlur={(e) => {
                const val = e.target.value;
                if (val === "") {
                  handleNumberInputWithLimits(
                    "height",
                    String(MIN_HEIGHT),
                    MIN_HEIGHT,
                    safeBufferedMaxHeight
                  );
                } else {
                  handleNumberInputWithLimits(
                    "height",
                    String(parseInt(val, 10)),
                    MIN_HEIGHT,
                    safeBufferedMaxHeight
                  );
                }
              }}
              style={{
                width: "60px",
                padding: "6px 8px",
                marginLeft: "3px",
                border: "1px solid #ccc",
                borderRadius: "4px",
                fontSize: "14px",
              }}
            />
          </div>
        </div>
      </div>

      {/* Duplicate to Selected Location button */}
      {onToggleCloneMode && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            e.preventDefault();
            // Use setTimeout to ensure the toggle happens after event propagation is stopped
            setTimeout(() => {
              onToggleCloneMode();
            }, 0);
          }}
          style={{
            width: "100%",
            padding: "10px 20px",
            backgroundColor: isCloneMode ? "#3B82F6" : "#F3F4F6",
            color: isCloneMode ? "white" : "#374151",
            border: isCloneMode ? "1px solid #3B82F6" : "1px solid #D1D5DB",
            borderRadius: "6px",
            fontSize: "14px",
            fontWeight: 500,
            cursor: "pointer",
            transition: "all 0.2s ease",
            boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: "4px",
            marginBottom: "12px",
          }}
        >
          <CopyIcon
            width={16}
            height={19}
            fill={isCloneMode ? "white" : "#374151"}
          />
          <span>Duplicate to Selected Location</span>
        </button>
      )}

      {/* Properties Section */}
      <div
        style={{
          background: "#f7f7f7",
          borderRadius: "8px",
          padding: "16px",
          marginBottom: "12px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "16px",
            cursor: "pointer",
          }}
          onClick={() => setIsPropertiesExpanded(!isPropertiesExpanded)}
        >
          <h4 style={{ margin: "0", fontWeight: "normal", fontSize: "14px" }}>
            Properties
          </h4>
          <svg
            width="12"
            height="7"
            viewBox="0 0 14 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              transform: isPropertiesExpanded
                ? "rotate(0deg)"
                : "rotate(180deg)",
              transition: "transform 0.2s",
            }}
          >
            <path
              d="M1.61906 7.75961L0.734375 6.87492L7.36906 0.240234L14.0038 6.87492L13.1191 7.75961L7.36906 2.0168L1.61906 7.75961Z"
              fill="#6C757D"
            />
          </svg>
        </div>

        {isPropertiesExpanded && (
          <>
            {/* Stroke Size */}
            <div
              style={{
                marginBottom: "12px",
                display: "flex",
                gap: "16px",
                alignItems: "center",
              }}
            >
              <label style={{ whiteSpace: "nowrap", fontSize: "13px" }}>
                Stroke Size
              </label>
              <input
                type="number"
                min={MIN_STROKE_WIDTH}
                max={MAX_STROKE_WIDTH}
                value={localInputValues.strokeWidth}
                onKeyDown={handleEnterKey}
                onChange={(e) => {
                  const { value } = e.target;
                  const inputType = e.nativeEvent?.inputType;
                  setLocalInputValues((prev) => ({
                    ...prev,
                    strokeWidth: value,
                  }));
                  if (value === "" || shouldDeferInputUpdate(inputType)) {
                    return;
                  }
                  const clamped = clampNumericValue(
                    value,
                    MIN_STROKE_WIDTH,
                    MAX_STROKE_WIDTH
                  );
                  if (clamped === null) {
                    return;
                  }
                  handleNumberInputWithLimits(
                    "strokeWidth",
                    String(clamped),
                    MIN_STROKE_WIDTH,
                    MAX_STROKE_WIDTH
                  );
                  setLocalInputValues((prev) => ({
                    ...prev,
                    strokeWidth: clamped,
                  }));
                }}
                onBlur={(e) => {
                  const val = e.target.value;
                  if (val === "") {
                    handleNumberInputWithLimits(
                      "strokeWidth",
                      String(MIN_STROKE_WIDTH),
                      MIN_STROKE_WIDTH,
                      MAX_STROKE_WIDTH
                    );
                  } else {
                    handleNumberInputWithLimits(
                      "strokeWidth",
                      String(parseInt(val, 10)),
                      MIN_STROKE_WIDTH,
                      MAX_STROKE_WIDTH
                    );
                  }
                }}
                style={{
                  width: "100%",
                  padding: "4px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                }}
              />
            </div>

            {/* Bends */}
            <div
              style={{
                marginBottom: "12px",
                display: "flex",
                gap: "16px",
                alignItems: "center",
              }}
            >
              <label style={{ whiteSpace: "nowrap", fontSize: "13px" }}>
                Bends
              </label>
              <input
                type="number"
                min="0"
                value={localInputValues.bends}
                onKeyDown={handleEnterKey}
                onChange={(e) => {
                  const { value } = e.target;
                  const inputType = e.nativeEvent?.inputType;
                  setLocalInputValues((prev) => ({
                    ...prev,
                    bends: value,
                  }));
                  if (value === "" || shouldDeferInputUpdate(inputType)) {
                    return;
                  }
                  const clamped = clampNumericValue(value, 0, undefined);
                  if (clamped === null) {
                    return;
                  }
                  handleNumberInputWithLimits(
                    "bends",
                    String(clamped),
                    0,
                    undefined
                  );
                  setLocalInputValues((prev) => ({
                    ...prev,
                    bends: clamped,
                  }));
                }}
                onBlur={(e) => {
                  const val = e.target.value;
                  if (val === "") {
                    handleNumberInputWithLimits(
                      "bends",
                      String(0),
                      0,
                      undefined
                    );
                  } else {
                    handleNumberInputWithLimits(
                      "bends",
                      String(parseInt(val, 10)),
                      0,
                      undefined
                    );
                  }
                }}
                style={{
                  width: "100%",
                  padding: "4px",
                  border: "1px solid #ccc",
                  borderRadius: "4px",
                }}
              />
            </div>

            {/* Color */}
            <div
              style={{
                marginBottom: "12px",
                display: "flex",
                gap: "16px",
                alignItems: "center",
              }}
            >
              <label style={{ whiteSpace: "nowrap", fontSize: "13px" }}>
                Color
              </label>
              <div
                style={{ display: "flex", gap: "8px", alignItems: "center" }}
              >
                <button
                  onClick={() => {
                    const newColor = "#dc3545";
                    updateElementProperties(selectedElement.id, {
                      strokeColor: newColor,
                      stroke: newColor,
                    });
                    setLocalInputValues((prev) => ({
                      ...prev,
                      colorHex: newColor.toUpperCase().replace("#", ""),
                    }));
                  }}
                  style={{
                    width: "24px",
                    height: "24px",
                    backgroundColor: "#dc3545",
                    border: "1px solid #ccc",
                    borderRadius: "2px",
                    cursor: "pointer",
                  }}
                />
                <button
                  onClick={() => {
                    const newColor = "#fd7e14";
                    updateElementProperties(selectedElement.id, {
                      strokeColor: newColor,
                      stroke: newColor,
                    });
                    setLocalInputValues((prev) => ({
                      ...prev,
                      colorHex: newColor.toUpperCase().replace("#", ""),
                    }));
                  }}
                  style={{
                    width: "24px",
                    height: "24px",
                    backgroundColor: "#fd7e14",
                    border: "1px solid #ccc",
                    borderRadius: "2px",
                    cursor: "pointer",
                  }}
                />
                <input
                  type="color"
                  value={strokeColor}
                  onChange={(e) => {
                    const newColor = e.target.value;
                    updateElementProperties(selectedElement.id, {
                      strokeColor: newColor,
                      stroke: newColor,
                    });
                    setLocalInputValues((prev) => ({
                      ...prev,
                      colorHex: newColor.toUpperCase().replace("#", ""),
                    }));
                  }}
                  style={{
                    width: "40px",
                    height: "24px",
                    border: "1px solid #ccc",
                    borderRadius: "2px",
                    cursor: "pointer",
                    padding: 0,
                  }}
                />
                <input
                  type="text"
                  value={`${localInputValues.colorHex}`}
                  maxLength={10}
                  onKeyDown={handleEnterKey}
                  onChange={(e) => {
                    let inputValue = e.target.value
                      .replace(/^Hex\s*/i, "")
                      .toUpperCase()
                      .replace("#", "");
                    if (inputValue.length > 6) {
                      inputValue = inputValue.slice(0, 6);
                    }
                    setLocalInputValues((prev) => ({
                      ...prev,
                      colorHex: inputValue,
                    }));
                  }}
                  onBlur={(e) => {
                    const hexValue = e.target.value.replace(/^Hex\s*/i, "");
                    const validColor = validateAndParseHexColor(hexValue);

                    if (validColor) {
                      updateElementProperties(selectedElement.id, {
                        strokeColor: validColor,
                        stroke: validColor,
                      });
                    } else {
                      const currentHex = strokeColor
                        .toUpperCase()
                        .replace("#", "");
                      setLocalInputValues((prev) => ({
                        ...prev,
                        colorHex: currentHex,
                      }));
                    }
                  }}
                  placeholder="Hex 000000"
                  style={{
                    flex: 1,
                    padding: "4px 8px",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    backgroundColor: "white",
                    fontSize: "12px",
                  }}
                />
              </div>
            </div>

            {/* Arrow Type Checkboxes */}
            <div
              style={{
                marginBottom: "12px",
                display: "flex",
                flexDirection: "row",
                gap: "10px",
                alignItems: "center",
              }}
            >
              <div
                style={{
                  width: "71px",
                  height: "28px",
                  borderRadius: "4px",
                  border: "1px solid #DEE2E6",
                  padding: "2px 4px",
                  opacity: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
                onClick={(e) => {
                  // Only handle if click is on the container, not the checkbox itself
                  if (e.target.type !== "checkbox") {
                    handleArrowTypeChange(
                      "hasStartArrow",
                      !localInputValues.hasStartArrow,
                      e
                    );
                  }
                }}
              >
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    width: "100%",
                    height: "100%",
                    margin: 0,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={localInputValues.hasStartArrow}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleArrowTypeChange("hasStartArrow", e.target.checked, e);
                    }}
                    style={{
                      width: "16px",
                      height: "16px",
                      cursor: "pointer",
                      marginRight: "8px",
                    }}
                  />
                  <div style={{ transform: "scaleX(-1)" }}>
                    <ArrowEndIcon width={25} height={12} fill="#686868" />
                  </div>
                </label>
              </div>
              <div
                style={{
                  width: "71px",
                  height: "28px",
                  borderRadius: "4px",
                  border: "1px solid #DEE2E6",
                  padding: "2px 4px",
                  opacity: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  cursor: "pointer",
                }}
                onClick={(e) => {
                  // Only handle if click is on the container, not the checkbox itself
                  if (e.target.type !== "checkbox") {
                    handleArrowTypeChange(
                      "hasEndArrow",
                      !localInputValues.hasEndArrow,
                      e
                    );
                  }
                }}
              >
                <label
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                    width: "100%",
                    height: "100%",
                    margin: 0,
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={localInputValues.hasEndArrow}
                    onChange={(e) => {
                      e.stopPropagation();
                      handleArrowTypeChange("hasEndArrow", e.target.checked, e);
                    }}
                    style={{
                      width: "16px",
                      height: "16px",
                      cursor: "pointer",
                      marginRight: "8px",
                    }}
                  />
                  <ArrowEndIcon width={25} height={12} fill="#686868" />
                </label>
              </div>
            </div>

            <button
              onClick={saveCurrentElementStyleAsPreset}
              disabled={Boolean(editingPreset)}
              style={{
                background: "#f7f7f7",
                border: "none",
                width: "100%",
                color: "#007bff",
                fontSize: "13px",
                opacity: editingPreset ? 0.5 : 1,
                cursor: editingPreset ? "not-allowed" : "pointer",
              }}
            >
              <span
                style={{
                  fontSize: "16px",
                  marginRight: "8px",
                }}
              >
                +
              </span>
              Add These Properties as a Style
            </button>
          </>
        )}
      </div>

      {/* Preset Controls */}
      <div
        style={{
          background: "#f7f7f7",
          borderRadius: "5px",
          marginTop: "-10px",
          padding: "1rem",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            cursor: "pointer",
          }}
          onClick={() => setIsElementPresetsExpanded(!isElementPresetsExpanded)}
        >
          <h4 style={{ margin: "0", fontWeight: "normal" }}>Styles</h4>
          <svg
            width="20"
            height="12"
            viewBox="0 0 14 8"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
            style={{
              transform: isElementPresetsExpanded
                ? "rotate(0deg)"
                : "rotate(180deg)",
              transition: "transform 0.2s",
            }}
          >
            <path
              d="M1.61906 7.75961L0.734375 6.87492L7.36906 0.240234L14.0038 6.87492L13.1191 7.75961L7.36906 2.0168L1.61906 7.75961Z"
              fill="#6C757D"
            />
          </svg>
        </div>

        {isElementPresetsExpanded && (
          <>
            {elementPresets.map((preset) => (
              <div key={preset.id}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "10px 0",
                    borderBottom: "1px solid #eee",
                  }}
                >
                <button
                  type="button"
                  onClick={() => {
                    if (editingPreset && editingPreset.id !== preset.id) return;
                    applyElementPreset(preset);
                  }}
                  disabled={
                    Boolean(editingPreset) && editingPreset.id !== preset.id
                  }
                  style={{
                    background: "none",
                    border: "none",
                    cursor:
                      editingPreset && editingPreset.id !== preset.id
                        ? "not-allowed"
                        : "pointer",
                    textAlign: "left",
                    flexGrow: 1,
                    padding: 0,
                    fontSize: "1rem",
                    color:
                      editingPreset && editingPreset.id !== preset.id
                        ? "#9ca3af"
                        : "inherit",
                    opacity:
                      editingPreset && editingPreset.id !== preset.id
                        ? 0.55
                        : 1,
                  }}
                >
                  {preset.name}
                </button>
                <div style={{ display: "flex", gap: "10px" }}>
                  <button
                    type="button"
                    disabled
                    title="Default is set in edit panel"
                    style={{
                      backgroundColor:
                        String(preset.id) === String(defaultPresetId)
                          ? "#2563eb"
                          : "#e5e7eb",
                      color:
                        String(preset.id) === String(defaultPresetId)
                          ? "white"
                          : "#111827",
                      border: "none",
                      borderRadius: "4px",
                      cursor: "not-allowed",
                      padding: "2px 6px",
                      fontSize: "11px",
                      opacity: 0.45,
                    }}
                  >
                    Default
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (editingPreset) return;
                      setEditingPreset(preset);
                      setPendingDefaultPresetId(defaultPresetId);
                      setDefaultPresetIdBeforeEdit(defaultPresetId);
                    }}
                    disabled={Boolean(editingPreset)}
                    title="Edit"
                    style={{
                      backgroundColor: "#007bff",
                      border: "none",
                      borderRadius: "4px",
                      cursor: editingPreset ? "not-allowed" : "pointer",
                      padding: "2px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "22px",
                      height: "22px",
                      opacity: editingPreset ? 0.45 : 1,
                    }}
                  >
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 12 12"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <g clipPath="url(#clip0_15909_16836)">
                        <path
                          d="M11.6263 1.45481C11.6964 1.5251 11.7357 1.6203 11.7357 1.71956C11.7357 1.81881 11.6964 1.91401 11.6263 1.98431L10.844 2.76731L9.34404 1.26731L10.1263 0.484307C10.1966 0.414005 10.292 0.374512 10.3914 0.374512C10.4908 0.374512 10.5862 0.414005 10.6565 0.484307L11.6263 1.45406V1.45481ZM10.3138 3.29681L8.81379 1.79681L3.70404 6.90731C3.66276 6.94858 3.63168 6.99891 3.61329 7.05431L3.00954 8.86481C2.99859 8.89781 2.99703 8.93321 3.00505 8.96705C3.01306 9.00089 3.03033 9.03184 3.05492 9.05643C3.07951 9.08102 3.11045 9.09828 3.14429 9.1063C3.17813 9.11431 3.21353 9.11276 3.24654 9.10181L5.05704 8.49806C5.11236 8.47988 5.16269 8.44906 5.20404 8.40806L10.3138 3.29681Z"
                          fill="white"
                        />
                        <path
                          fillRule="evenodd"
                          clipRule="evenodd"
                          d="M0.75 10.125C0.75 10.4234 0.868526 10.7095 1.0795 10.9205C1.29048 11.1315 1.57663 11.25 1.875 11.25H10.125C10.4234 11.25 10.7095 11.1315 10.9205 10.9205C11.1315 10.7095 11.25 10.4234 11.25 10.125V5.625C11.25 5.52554 11.2105 5.43016 11.1402 5.35984C11.0698 5.28951 10.9745 5.25 10.875 5.25C10.7755 5.25 10.6802 5.28951 10.6098 5.35984C10.5395 5.43016 10.5 5.52554 10.5 5.625V10.125C10.5 10.2245 10.4605 10.3198 10.3902 10.3902C10.3198 10.4605 10.2245 10.5 10.125 10.5H1.875C1.77554 10.5 1.68016 10.4605 1.60984 10.3902C1.53951 10.3198 1.5 10.2245 1.5 10.125V1.875C1.5 1.77554 1.53951 1.68016 1.60984 1.60984C1.68016 1.53951 1.77554 1.5 1.875 1.5H6.75C6.84946 1.5 6.94484 1.46049 7.01517 1.39017C7.08549 1.31984 7.125 1.22446 7.125 1.125C7.125 1.02554 7.08549 0.930161 7.01517 0.859835C6.94484 0.789509 6.84946 0.75 6.75 0.75H1.875C1.57663 0.75 1.29048 0.868526 1.0795 1.0795C0.868526 1.29048 0.75 1.57663 0.75 1.875V10.125Z"
                          fill="white"
                        />
                      </g>
                      <defs>
                        <clipPath id="clip0_15909_16836">
                          <rect width="12" height="12" fill="white" />
                        </clipPath>
                      </defs>
                    </svg>
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      if (editingPreset || elementPresets.length <= 1) return;
                      const filtered = elementPresets.filter(
                        (p) => p.id !== preset.id
                      );
                      setElementPresets(filtered);
                      saveElementPresetsToStorage(filtered);
                      const storedDefaultId = localStorage.getItem(
                        DEFAULT_PRESET_ID_STORAGE_KEY,
                      );
                      if (
                        String(defaultPresetId) === String(preset.id) ||
                        (storedDefaultId != null &&
                          String(storedDefaultId) === String(preset.id))
                      ) {
                        saveDefaultPresetToStorage(filtered[0]);
                      }
                      if (editingPreset && editingPreset.id === preset.id) {
                        setEditingPreset(null);
                        setPendingDefaultPresetId(null);
                        setDefaultPresetIdBeforeEdit(null);
                      }
                    }}
                    disabled={Boolean(editingPreset) || elementPresets.length <= 1}
                    title="Delete"
                    style={{
                      backgroundColor: "#dc3545",
                      border: "none",
                      borderRadius: "4px",
                      cursor:
                        editingPreset || elementPresets.length <= 1
                          ? "not-allowed"
                          : "pointer",
                      padding: "2px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      width: "22px",
                      height: "22px",
                      opacity:
                        editingPreset || elementPresets.length <= 1 ? 0.45 : 1,
                    }}
                  >
                    <svg
                      width="18"
                      height="18"
                      viewBox="0 0 18 18"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <rect width="18" height="18" rx="4" fill="#DC3545" />
                      <path
                        d="M5.875 14.5C5.53125 14.5 5.23698 14.3803 4.99219 14.141C4.7474 13.9016 4.625 13.6139 4.625 13.2778V5.33333H4V4.11111H7.125V3.5H10.875V4.11111H14V5.33333H13.375V13.2778C13.375 13.6139 13.2526 13.9016 13.0078 14.141C12.763 14.3803 12.4688 14.5 12.125 14.5H5.875ZM7.125 12.0556H8.375V6.55556H7.125V12.0556ZM9.625 12.0556H10.875V6.55556H9.625V12.0556Z"
                        fill="white"
                      />
                    </svg>
                  </button>
                </div>
                </div>
                {editingPreset && editingPreset.id === preset.id && (
                  <div
                    style={{
                      marginTop: "8px",
                      marginBottom: "12px",
                      padding: "12px",
                      border: "1px solid #ccc",
                      borderRadius: "6px",
                    }}
                  >
                    <h4>Edit Preset: {editingPreset.name}</h4>
                    <input
                      type="text"
                      value={editingPreset.name}
                      onChange={(e) =>
                        setEditingPreset({
                          ...editingPreset,
                          name: e.target.value,
                        })
                      }
                      placeholder="Preset name"
                      style={{
                        width: "100%",
                        padding: "6px",
                        border: "1px solid #ccc",
                        marginBottom: "12px",
                      }}
                    />
                    <label
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "8px",
                        marginBottom: "12px",
                        fontSize: "13px",
                      }}
                    >
                      <input
                        type="checkbox"
                        checked={
                          String(editingPreset.id) ===
                          String(pendingDefaultPresetId ?? defaultPresetId)
                        }
                        onChange={(e) => {
                          if (e.target.checked) {
                            setPendingDefaultPresetId(String(editingPreset.id));
                          }
                        }}
                      />
                      Set as default preset
                    </label>
                    <button
                      onClick={() => {
                        const trimmedName = editingPreset.name.trim();
                        const isDuplicate = elementPresets.some(
                          (p) =>
                            p.id !== editingPreset.id &&
                            p.name.trim().toLowerCase() ===
                              trimmedName.toLowerCase()
                        );
                        if (isDuplicate) {
                          alert(
                            "A preset with this name already exists. Please choose a different name."
                          );
                          return;
                        }
                        const updated = elementPresets.map((p) =>
                          p.id === editingPreset.id
                            ? { ...editingPreset, name: trimmedName }
                            : p
                        );
                        const nextDefaultPresetId =
                          pendingDefaultPresetId ?? defaultPresetId;
                        const nextDefaultPreset = updated.find(
                          (p) =>
                            String(p.id) === String(nextDefaultPresetId),
                        );
                        setElementPresets(updated);
                        saveElementPresetsToStorage(updated);
                        if (nextDefaultPreset) {
                          saveDefaultPresetToStorage(nextDefaultPreset);
                        }
                        setEditingPreset(null);
                        setPendingDefaultPresetId(null);
                        setDefaultPresetIdBeforeEdit(null);
                        setPendingNewPresetId(null);
                      }}
                      style={{
                        backgroundColor: "#2563eb",
                        color: "white",
                        padding: "6px 12px",
                        borderRadius: "4px",
                        border: "none",
                        cursor: "pointer",
                        marginRight: "8px",
                      }}
                    >
                      Save Changes
                    </button>
                    <button
                      onClick={() => {
                        const rolledBack = pendingNewPresetId
                          ? elementPresets.filter(
                              (p) => p.id !== pendingNewPresetId,
                            )
                          : elementPresets;
                        if (pendingNewPresetId) {
                          setElementPresets(rolledBack);
                        }
                        const fallbackDefaultId =
                          defaultPresetIdBeforeEdit ?? defaultPresetId;
                        const restoredDefaultPreset =
                          rolledBack.find(
                            (p) =>
                              String(p.id) === String(fallbackDefaultId),
                          ) || rolledBack[0];
                        if (restoredDefaultPreset) {
                          saveDefaultPresetToStorage(restoredDefaultPreset);
                        }
                        setEditingPreset(null);
                        setPendingDefaultPresetId(null);
                        setDefaultPresetIdBeforeEdit(null);
                        setPendingNewPresetId(null);
                      }}
                      style={{
                        backgroundColor: "#e5e7eb",
                        color: "#111827",
                        padding: "6px 12px",
                        borderRadius: "4px",
                        border: "none",
                        cursor: "pointer",
                      }}
                    >
                      Cancel
                    </button>
                  </div>
                )}
              </div>
            ))}
          </>
        )}
      </div>

      {/* Delete Button */}
      <div style={{ marginTop: "16px" }}>
        <button
          onClick={onDelete}
          style={{
            width: "100%",
            height: "40px",
            backgroundColor: "white",
            borderRadius: "5px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "pointer",
            border: "2px solid red",
            color: "red",
            marginTop: "10px",
          }}
        >
          Delete Stroke
        </button>
      </div>
    </div>
  );
}

UnifiedLineArrowEditor.propTypes = {
  selectedElement: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    x: PropTypes.number,
    y: PropTypes.number,
    length: PropTypes.number,
    angle: PropTypes.number,
    strokeColor: PropTypes.string,
    strokeWidth: PropTypes.number,
    isDashed: PropTypes.bool,
    points: PropTypes.arrayOf(PropTypes.number),
    hasStartArrow: PropTypes.bool,
    hasEndArrow: PropTypes.bool,
  }).isRequired,
  updateElementProperties: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
  canvasHeight: PropTypes.string,
};

UnifiedLineArrowEditor.defaultProps = {
  selectedElement: null,
};

export default UnifiedLineArrowEditor;
